import Vue from "vue"
import Axios from "axios"
import { Component } from "vue-property-decorator"
import { sleep } from "@/helpers/helperSleep"
import { protocol } from "@/helpers/helperString"
import { copy } from "@/helpers/helperCopy"
import { debounce } from "@/helpers/helperDebounce"
import { error as catchError } from "@/helpers/treatmentErrorEventCodes"
import { mixinImplementRef } from "@/mixins/mixinImplementRef"
import { mixins } from "vue-class-component"
import { currentUser, event, shrink, signInAnonimousUser } from "@/plugins/firebase/firebasePluginConfig"
import { installed } from "@/helpers/helperInstalled"

Component.registerHooks([
  "beforeRouteEnter",
])

@Component({})
export class mixinViewHome extends mixins(
  mixinImplementRef,
) {
  copy = copy
  debounce = debounce
  installed = installed()

  beforeRouteEnter (
    to: {
      meta: {
        url: string;
      }
    },
    _from: never,
    next: (arg0?: (vm: mixinViewHome) => void) => void,
  ): void {
    next(async (vm) => {
      try {
        const {
          url,
        } = to.meta || {}

        if (url) {
          /* v 0.1
          const {
            modelInputsListUrl,
          } = vm.$refs || {}
          if (!modelInputsListUrl) await sleep(1)
          Vue.set(modelInputsListUrl, "internalValue", url)
          */

          /* v 0.2
          const { internalValue } = vm.ref("modelInputsListUrl") as {
            internalValue: string;
          } || {}
          if (!internalValue) await sleep(1)
          */
          vm.ref("modelInputsListUrl", (v) => v && Vue.set(v, "internalValue", url))
        }
      } catch (error) {
        catchError("error on beforeRouteEnter", error)
      }
    })
  }

  urlPreview: URL = {} as URL
  urlShortcutShrink = ""
  urlShortcutPath = ""
  get urlShortcut (): string {
    const {
      VUE_APP_URL_SHRINK_APP
    } = process.env

    const {
      href,
    } = new URL(this.urlShortcutShrink, protocol(VUE_APP_URL_SHRINK_APP) ? VUE_APP_URL_SHRINK_APP : `https://${VUE_APP_URL_SHRINK_APP}`)

    return this.urlShortcutShrink ? href : ""
  }

  async urlShortcutAction () {
    let urlPreview = this.urlPreview.href
    const {
      internalValue,
    } = this.$refs?.modelInputsListUrl ?? {}

    if (!this.viewAdvancedMode) {
      urlPreview = internalValue
    }

    const TEST = false

    if (TEST) {
      this.urlShortcutShrink = "123456"
      return
    }

    let {
      uid
    } = currentUser() || {}

    const {
      VUE_APP_SHORTIO_PUBLIC_KEY,
    } = process?.env ?? {}

    if (!uid) await signInAnonimousUser()
      .then(({ uid: anonimousUserUid }) => { uid = anonimousUserUid })

    try {
      await Axios.post("https://api.short.io/links/public",
        {
          domain: "poj.es",
          originalURL: urlPreview,
        },
        {
          headers: {
            Authorization: VUE_APP_SHORTIO_PUBLIC_KEY
          }
        }
      )
        .then(({ data }) => {
          const {
            path,
          } = data || {}

          this.urlShortcutPath = path
        })
    } catch (error) {
      event("error_shrink_url - api.short.io", { error })
    }

    shrink({
      uid,
      "path": this.urlShortcutPath,
      "url": urlPreview,
    })
      .then((url) => {
        event("created_url", { url })
        this.urlShortcutShrink = url
      })
      .catch((error) => {
        event("error_shrink_url", { error })
      })
  }

  get modelInputsListUrlPrefix (): string {
    const { internalValue } = this.ref("modelInputsListUrl") as {
      internalValue: string;
    } || this.queryBuilderInputs
    return protocol(internalValue as string) ? "" : "https://"
  }

  queryBuilderInputs: Record<string, {
    title: string;
    id: string;
    alt: string;
  }> = {
    "utm_source": {
      "id": "utm_source",
      "title": "Source",
      "alt": "Valor do utm_source"
    },
    "utm_medium": {
      "id": "utm_medium",
      "title": "Medium",
      "alt": "Valor do utm_medium"
    },
    "utm_campaign": {
      "id": "utm_campaign",
      "title": "Campaign",
      "alt": "Valor do utm_campaign"
    },
  }

  searchInput (key: { internalValue: string; }): boolean {
    const { internalValue } = key || {}
    return !!this.queryBuilderInputs[internalValue]
  }

  async createNewInput (key: {
    internalValue: string;
    id?: string;
  }, value?: string): Promise<void> {
    const {
      id,
      internalValue,
    } = key || {}
    if (!internalValue) return

    if (!this.searchInput(key)) {
      Vue.set(this.queryBuilderInputs, internalValue, {
        "id": internalValue,
        "title": internalValue,
        "alt": `Valor de ${internalValue}`,
      })
    }

    if (!this.ref(`query_input_${internalValue}`)) await sleep(1)

    this.ref(`query_input_${internalValue}`, (v) => v && Vue.set(v, "internalValue", value))

    if (id === "createnewinput") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.ref(`query_input_${internalValue}`, (v) => v && (v as any).focus())
    }
  }

  changeInput (id: string, v?: string): void {
    try {
      id = String(id || "");
      v = String(v || "");

      switch (id) {
        case "url":
          if (!v) break

          if (v.length > 8) {
            if (!protocol(v)) v = `https://${v}`
            const { origin, pathname, searchParams } = new URL(v) as URL

            // https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
            this.urlPreview = new URL(pathname, origin)

            if (searchParams.size) {
              // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
              for (const [key, value] of searchParams.entries()) {
                if (value) this.createNewInput({ "internalValue": key }, value)
              }
            }
          }

        break

        default:
          if (!this.searchInput({ "internalValue": id })) break
          this.createUrlPreview(id, v)
      }
    } catch (error) {
      catchError("error on changeInput", error)
    }
  }

  removeInput (id: string) {
    id = String(id)
    if (!id) return

    const {
      searchParams,
    } = this.urlPreview

    searchParams.delete(id)
    Vue.delete(this.queryBuilderInputs, id)
  }

  createUrlPreview (id: string, v: string): void {
    id = String(id)
    v = String(v)
    if (!id) return

    const {
      searchParams,
    } = this.urlPreview
    v ? searchParams.set(id, v) : searchParams.delete(id)
    Vue.set(this.queryBuilderInputs[id], "value", v)
  }

  showInputLabelRefMessage (key: string): boolean {
    const REF = this.ref(key) as {
      internalValue: string;
      value: string;
    }

    if (!REF) return false

    const {
      internalValue,
      value,
    } = REF

    return !!(internalValue || value || "")
  }

  toggleAdvancedModePanel: number[] = []

  toggleAdvancedMode () {
    if (this.toggleAdvancedModePanel.length === 0) {
      this.toggleAdvancedModePanel = [0]
    } else {
      this.toggleAdvancedModePanel = []
    }
  }

  get viewAdvancedMode (): boolean {
    return (this.toggleAdvancedModePanel?.length !== 0)
  }

  restartApp (): void {
    location.reload()
  }
}
